import React from 'react';
import { AppBar, AppGnb } from './AppShell';
import styles from './TabShell.module.css';

/**
 * 탭 화면 셸 — 홈 · 주요기능 · 채팅 · 설정이 공유합니다.
 *
 * 탭 화면은 서브 화면과 달리 뒤로가기가 없고, 하단 탭이 항상 있으며,
 * 스크롤 컨테이너를 직접 잡아야 합니다(당겨서 새로고침이 그 위에서 동작).
 * 그 골격을 네 화면이 각자 짜면 여백과 상단바 높이가 갈라지므로 여기 하나로 둡니다.
 *
 *     <TabShell title="주요기능" scrollRef={pullRef} pullDistance={d} refreshing={r}>
 *         ...섹션들...
 *     </TabShell>
 */

interface TabShellProps {
    /** 상단바 가운데 글자 */
    title: string;
    children: React.ReactNode;
    /** 상단바 우측 액션 */
    right?: React.ReactNode;
    /** 당겨서 새로고침 — usePullToRefresh 의 containerRef */
    scrollRef?: React.RefObject<HTMLDivElement | null>;
    pullDistance?: number;
    refreshing?: boolean;
    /** 채팅 탭 알림 점 */
    chatDot?: boolean;
    /** 하단 탭 위에 떠 있는 것 (FAB 등) */
    floating?: React.ReactNode;
}

export const TabShell = ({
    title, children, right, scrollRef, pullDistance = 0, refreshing, chatDot, floating,
}: TabShellProps) => (
    <div className={styles.wrap}>
        <div className={styles.bar}>
            <AppBar mode="plain" title={title} right={right} />
        </div>

        <div className={styles.scroll} ref={scrollRef as React.RefObject<HTMLDivElement>}>
            {scrollRef ? (
                <div className={styles.ptr} style={{ height: pullDistance > 0 ? pullDistance : 0 }}>
                    <span
                        className={refreshing ? `${styles.ptrSpinner} ${styles.ptrSpinning}` : styles.ptrSpinner}
                        style={!refreshing ? { transform: `rotate(${pullDistance * 4}deg)` } : undefined}
                    />
                </div>
            ) : null}

            {children}

            <div className={styles.tail} />
        </div>

        {floating}
        <AppGnb chatDot={chatDot} />
    </div>
);

/** 상단바 우측 아이콘 버튼 — 탭 화면들이 같은 크기를 쓰도록 */
export const TabBarButton = ({
    onClick, label, children,
}: { onClick: () => void; label: string; children: React.ReactNode }) => (
    <button type="button" className={styles.barBtn} onClick={onClick} aria-label={label}>
        {children}
    </button>
);

export const TabBarActions = ({ children }: { children: React.ReactNode }) => (
    <span className={styles.barActions}>{children}</span>
);
